import React, { useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useRolePermissions, useSnackbar } from '../../hooks';

import { UploadType } from '../../types/upload.types';
import {
  UploadTypeSelector,
  UploadFileInput,
  CreateOrderCheckbox,
  NotificationOptions,
  UploadButton
} from './components';

import './UploadPage.css';

import {
  useUploadOkkoPricesMutation,
  useUploadCompetitorPricesMutation,
  useUploadDiscountsMutation,
  useUploadCompetitorsDataMutation
} from '@/services/upload.api';
import { formatIntegrationMessage } from '@/utils/integrationUtils';
import { defaultSends, getDefaultDateTime, getErrorMessage } from './upload.utils';

function UploadPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canUploadOkko, canUploadCompetitors, canUploadCompetitorsData } = useRolePermissions();
  const { setSnackbarMessage } = useSnackbar();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedType, setSelectedType] = useState<UploadType | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [createOrder, setCreateOrder] = useState<boolean>(false);
  const [sends, setSends] = useState<typeof defaultSends>(defaultSends);
  const [notificationDateTime, setNotificationDateTime] = useState<string>(() => getDefaultDateTime());

  const [uploadOkkoPrices, { isLoading: isUploadingOkkoPrices }] = useUploadOkkoPricesMutation();
  const [uploadCompetitorPrices, { isLoading: isUploadingCompetitorPrices }] = useUploadCompetitorPricesMutation();
  const [uploadDiscounts, { isLoading: isUploadingDiscounts }] = useUploadDiscountsMutation();
  const [uploadCompetitorsData, { isLoading: isUploadingCompetitorsData }] = useUploadCompetitorsDataMutation();

  const uploading =
    isUploadingOkkoPrices || isUploadingCompetitorPrices || isUploadingDiscounts || isUploadingCompetitorsData;
  const hasOrderOptions = selectedType === UploadType.OKKO_PRICES;
  const hasActiveNotifications = sends.sendSMS || sends.sendMail;
  const isUploadEnabled = selectedType !== null && selectedFile !== null && !uploading;
  const areInputsEnabled = selectedType !== null && !uploading;

  const handleInitializeDateTime = () => {
    setNotificationDateTime(getDefaultDateTime());
  };

  const handleOnNotificationChange = () => {
    if (!hasActiveNotifications) {
      handleInitializeDateTime();
    }
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCreateOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCreateOrder(checked);
    if (checked) {
      setSends({
        sendSMS: true,
        sendMail: true,
        send1C: true,
        sendOkkoCore: true
      });
    } else {
      setSends(defaultSends);
      handleInitializeDateTime();
    }
  };

  const handleSendCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name as keyof typeof sends;
    setSends(prev => ({ ...prev, [key]: e.target.checked }));
    if (key === 'sendSMS' || key === 'sendMail') {
      handleOnNotificationChange();
    }
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value as UploadType);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputClick = () => fileInputRef.current?.click();

  const handleOnUpload = async () => {
    if (!selectedFile || !selectedType) return;
    const handlers: Partial<Record<UploadType, () => Promise<any>>> = {
      [UploadType.OKKO_PRICES]: () =>
        uploadOkkoPrices({
          file: selectedFile,
          options: {
            createOrder,
            ...sends,
            notificationDateTime
          }
        }).unwrap(),
      [UploadType.COMPETITOR_PRICES]: () => uploadCompetitorPrices(selectedFile).unwrap(),
      [UploadType.DISCOUNTS]: () => uploadDiscounts(selectedFile).unwrap(),
      [UploadType.COMPETITORS_DATA]: () => uploadCompetitorsData(selectedFile).unwrap()
    };

    const handler = handlers[selectedType];
    if (!handler) {
      setSnackbarMessage('Невідомий тип файлу', 'error');
      return;
    }

    try {
      const result = await handler();
      const formattedMessage = formatIntegrationMessage(result.message, result.scheduledNotifications);
      setSelectedFile(null);
      setSelectedType(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSnackbarMessage(formattedMessage, 'success', 8000, true);
    } catch (error: any) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);

      const errorMessage = getErrorMessage(error);
      setSnackbarMessage(errorMessage, 'error', 15000, true);
    }
  };

  const handleNotificationDateChange = (date: Date | null | string) => {
    setNotificationDateTime(date ? new Date(date as string).toISOString() : '');
  };

  return (
    <div className={`upload-main-container ${isMobile ? 'mobile' : ''}`}>
      <div className="upload-content-wrapper">
        <div className="upload-card">
          {/* Page Header */}
          <div className="upload-header">
            <h4 className="upload-title">Роздрібні ціни</h4>
          </div>

          {/* Radio buttons row */}
          <UploadTypeSelector canUploadOkko={canUploadOkko} canUploadCompetitors={canUploadCompetitors} canUploadCompetitorsData={canUploadCompetitorsData} selectedType={selectedType} disabled={uploading} onChange={handleTypeChange} />

          {/* File input row */}
          <UploadFileInput
            fileInputRef={fileInputRef}
            selectedFile={selectedFile}
            disabled={!areInputsEnabled}
            onFileSelected={handleFileSelected}
            onPickClick={handleInputClick}
          />

          {/* Create order checkbox */}
          <CreateOrderCheckbox
            hasOrderOptions={hasOrderOptions}
            disabled={!areInputsEnabled}
            checked={createOrder}
            onChange={handleCreateOrderChange}
          />

          {/* Notification options (shown when createOrder is true) */}

          <NotificationOptions
            sends={sends}
            disabled={!areInputsEnabled}
            notificationDateTime={notificationDateTime}
            datePickerDisabled={!hasActiveNotifications || uploading}
            hasOrderOptions={hasOrderOptions}
            isCreateOrder={createOrder}
            onSendChange={handleSendCheckbox}
            onNotificationDateChange={handleNotificationDateChange}
          />

          {/* Upload button */}

          <UploadButton disabled={!isUploadEnabled} uploading={uploading} onClick={handleOnUpload} />
        </div>
      </div>
    </div>
  );
}

export default UploadPage;
